import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Youtube, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useT } from "../../lib/i18n";

const LEGAL = [
  ["Terms of Use", "/legal/terms"],
  ["Privacy Policy", "/legal/privacy"],
  ["Community Guidelines", "/legal/community"],
  ["Cookie Notice", "/legal/cookies"],
  ["Advertising & Affiliate Disclosure", "/legal/advertising"],
  ["Copyright & DMCA", "/legal/dmca"],
  ["Contact & Report Content", "/legal/contact"],
  ["Accessibility Statement", "/legal/accessibility"],
  ["Account Deletion & Data Requests", "/legal/data-requests"],
  ["Do Not Sell or Share My Info", "/legal/do-not-sell"],
];

export function Footer() {
  const { t } = useT();
  const [social, setSocial] = useState({ linkedin: "", youtube: "" });
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    api.get("/settings/social").then((r) => setSocial(r.data || {})).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || subLoading) return;
    setSubLoading(true);
    try {
      await api.post("/newsletter", { email });
      setSubscribed(true);
      setEmail("");
      toast.success("You're subscribed to the PictureThisInk newsletter.");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    } finally {
      setSubLoading(false);
    }
  };

  const socials = [
    { key: "linkedin", url: social.linkedin, Icon: Linkedin, label: "Follow PictureThisInk on LinkedIn" },
    { key: "youtube", url: social.youtube, Icon: Youtube, label: "Follow PictureThisInk on YouTube" },
  ].filter((s) => s.url);

  return (
    <footer className="bg-pti-black text-white mt-24 pb-24 lg:pb-0" data-testid="site-footer">
      <div className="pti-container py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <img src="/logo-white.png" alt="PictureThisInk — Digital Media Publishing Platform" className="h-16 w-auto object-contain" />
          <p className="text-[11px] uppercase tracking-widest text-neutral-400 mt-2" data-testid="footer-powered-by">
            Powered by <a href="https://www.mishertech.com" target="_blank" rel="noopener noreferrer" aria-label="Visit MisherTech" className="font-bold text-neutral-200 hover:text-pti-red transition-colors">MisherTech</a>
          </p>
          <p className="text-sm text-neutral-400 mt-3 leading-relaxed">A premium creator media platform for technology, AI, creative business, design, innovation, and the people shaping the future.</p>
          <Link to="/advertise" data-testid="footer-advertise" className="inline-block mt-4 bg-pti-red text-white px-4 py-2 text-sm font-bold hover:bg-pti-redhover transition-colors">{t("advertiseWithUs")}</Link>
          <a href="mailto:corporate@mishertech.com" data-testid="footer-contact-email" className="mt-4 flex items-center gap-2 text-sm text-neutral-400 hover:text-pti-red transition-colors">
            <Mail size={14} aria-hidden="true" /> corporate@mishertech.com
          </a>
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-4">{t("explore")}</h3>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li><Link to="/articles" className="hover:text-pti-red">{t("articles")}</Link></li>
            <li><Link to="/shorts" className="hover:text-pti-red">{t("shorts")}</Link></li>
            <li><Link to="/inkwire" className="hover:text-pti-red">{t("inkwire")}</Link></li>
            <li><Link to="/pti-play" className="hover:text-pti-red">{t("play")}</Link></li>
            <li><Link to="/creators" className="hover:text-pti-red">{t("creators")}</Link></li>
          </ul>
          {socials.length > 0 && (
            <div className="mt-8 text-center md:text-left" data-testid="footer-social">
              <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">{t("follow")}</h3>
              <div className="flex items-center justify-center md:justify-start gap-3">
                {socials.map(({ key, url, Icon, label }) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}
                    data-testid={`footer-social-${key}`}
                    className="flex items-center justify-center w-10 h-10 rounded-full border border-neutral-700 text-neutral-300 hover:bg-pti-red hover:border-pti-red hover:text-white focus:bg-pti-red focus:border-pti-red focus:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pti-red focus-visible:ring-offset-2 focus-visible:ring-offset-pti-black transition-colors">
                    <Icon size={18} aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-4">{t("legal")}</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-neutral-300">
            {LEGAL.map(([label, to]) => (
              <li key={to}><Link to={to} data-testid={`footer-${to.split("/").pop()}`} className="hover:text-pti-red">{label}</Link></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-800">
        <div className="pti-container py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="footer-newsletter">
          <div className="flex items-center gap-2 text-center md:text-left justify-center md:justify-start">
            <Mail size={18} className="text-pti-red shrink-0" aria-hidden="true" />
            <p className="text-sm text-neutral-300"><span className="font-bold text-white">{t("newsletterTitle")}</span> — {t("newsletterDesc")}</p>
          </div>
          {subscribed ? (
            <p className="text-sm text-neutral-400 text-center md:text-right" data-testid="footer-newsletter-success">{t("subscribed")}</p>
          ) : (
            <form onSubmit={subscribe} className="flex w-full md:w-auto max-w-md mx-auto md:mx-0">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")} aria-label="Email address" data-testid="footer-newsletter-email"
                className="flex-1 md:w-64 bg-white/5 border border-neutral-700 px-3 py-2.5 text-sm text-white placeholder:text-neutral-500 focus:border-pti-red outline-none" />
              <button type="submit" disabled={subLoading} data-testid="footer-newsletter-submit"
                className="bg-pti-red text-white text-sm font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-pti-redhover transition-colors disabled:opacity-60 whitespace-nowrap">
                {subLoading ? "…" : t("subscribe")}
              </button>
            </form>
          )}
        </div>
      </div>
      <div className="border-t border-neutral-800">
        <div className="pti-container py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <p>© {new Date().getFullYear()} PictureThisInk. General-audience platform. You must be 18+ to create an account.</p>
          <p>Curated with care. Never scraped, never republished in full.</p>
        </div>
      </div>
    </footer>
  );
}
